<template>
  <b-card
    ref="participantsCard"
    title="Участники"
    class="mt-3 participants-card"
    :class="layoutClass"
  >
    <b-alert variant="info" show class="mb-3 hints-alert">
      <div class="d-flex align-items-start">
        <div class="hint-icon">💡</div>
        <div class="hint-content">
          <div class="hint-title">Импорт участников:</div>
          <div class="hint-list">
            <div class="hint-list-item">
              Подготовьте Excel файл (.xlsx или .xls)
            </div>
            <div class="hint-list-item">
              Основные столбцы: <code>Номер</code>, <code>Фамилия</code>, <code>Имя</code>, <code>Чип</code>
            </div>
            <div class="hint-list-item">
              Опциональные: <code>Группа</code>, <code>Команда</code>, <code>Пол</code>, <code>Дистанция</code>, <code>Год рождения</code>, <code>Дата старта</code> + <code>Стартовое время</code>
            </div>
            <div class="hint-list-item">
              Несколько чипов разделяйте запятой: <code>E280111..., E280112...</code>
            </div>
            <div class="hint-list-item">
              После импорта можно редактировать прямо в таблице
            </div>
          </div>
        </div>
      </div>
    </b-alert>

    <div class="participants-toolbar mb-3">
      <div class="participants-toolbar__left">
        <strong>Загружено участников:</strong> {{ localParticipants.length }}
        <span
          v-if="(participantSearchQuery.trim() || invalidDistanceOnly) && localParticipants.length"
          class="text-muted participants-toolbar__filter-meta"
        >
          · по фильтру: {{ filteredParticipants.length }}
        </span>
        <span
          v-if="invalidDistanceOnly && localParticipants.length"
          class="text-danger participants-toolbar__filter-meta"
        >
          · только с некорректной дистанцией ({{ invalidDistanceParticipantsCount }})
        </span>
      </div>
      <div class="participants-toolbar__search">
        <b-form-input
          v-model="participantSearchQuery"
          type="search"
          size="sm"
          placeholder="Поиск: номер, фамилия, имя, чип…"
          class="participants-search-input"
          autocomplete="off"
          aria-label="Поиск по участникам"
        />
      </div>
      <div class="participants-toolbar__buttons">
        <b-button
          v-if="invalidDistanceOnly"
          size="sm"
          variant="outline-secondary"
          class="participants-toolbar__btn"
          @click="clearInvalidDistanceFilter"
        >
          Показать всех
        </b-button>
        <b-button 
          v-b-tooltip.hover.top="'Импортировать список участников из Excel файла. Поддерживаются форматы .xlsx и .xls'"
          size="sm" 
          variant="primary" 
          class="participants-toolbar__btn"
          @click="showImportModal = true"
        >
          📥 Импорт из Excel
        </b-button>
        <b-button 
          v-b-tooltip.hover.top="'Добавить одного участника вручную. Данные можно редактировать в таблице.'"
          size="sm" 
          variant="outline-primary" 
          class="participants-toolbar__btn"
          @click="addParticipant"
        >
          + Добавить вручную
        </b-button>
      </div>
    </div>

    <!-- Participants table -->
    <div
      v-if="localParticipants.length > 0"
      ref="participantsTableWrap"
      class="table-responsive"
    >
      <template v-if="filteredParticipants.length > 0">
      <b-table
        :items="displayedParticipants"
        :fields="tableFields"
        table-class="participants-table"
        small
        striped
        hover
        sticky-header="400px"
      >
        <template #cell(actions)="{ index }">
          <div class="participants-cell-actions">
            <b-button size="sm" variant="danger" class="participants-btn-remove" @click="removeParticipant(index)">
              ✕
            </b-button>
          </div>
        </template>

        <template #cell(chipIds)="{ item, index }">
          <b-form-input
            v-model="item.chipIdsString"
            placeholder="E280..., E281..."
            size="sm"
            @blur="updateChipIds(index)"
          />
        </template>

        <template #cell(userId)="{ item, index }">
          <b-form-input v-model="item.userId" size="sm" @input="emitUpdate" />
        </template>

        <template #cell(secondName)="{ item, index }">
          <b-form-input v-model="item.secondName" size="sm" @input="emitUpdate" />
        </template>

        <template #cell(name)="{ item, index }">
          <b-form-input v-model="item.name" size="sm" @input="emitUpdate" />
        </template>

        <template #cell(group)="{ item, index }">
          <b-form-input v-model="item.group" size="sm" @input="emitUpdate" />
        </template>

        <template #cell(team)="{ item, index }">
          <b-form-input v-model="item.team" size="sm" @input="emitUpdate" />
        </template>

        <template #cell(sex)="{ item, index }">
          <b-form-select
            v-model="item.sex"
            :options="sexOptions"
            size="sm"
            @change="emitUpdate"
          />
        </template>

        <template #cell(distance)="{ item, index }">
          <b-form-input v-model="item.distance" size="sm" @input="handleDistanceInput(index)" />
        </template>

        <template #cell(birthday)="{ item, index }">
          <b-form-input v-model="item.birthday" size="sm" type="text" placeholder="YYYY" @input="emitUpdate" />
        </template>

        <template #cell(startTime)="{ item, index }">
          <b-form-input
            :value="participantStartTimeInputValue(item)"
            type="datetime-local"
            size="sm"
            class="participant-start-time-input"
            @input="onParticipantStartTimeInput(index, $event)"
          />
        </template>
      </b-table>
      </template>
      <div v-else class="text-center py-4 participants-search-empty">
        <p class="mb-2">
          Ничего не найдено по запросу «<strong>{{ participantSearchQuery.trim() }}</strong>»
        </p>
        <b-button size="sm" variant="link" class="p-0" @click="participantSearchQuery = ''">
          Сбросить поиск
        </b-button>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredParticipants.length"
          :per-page="perPage"
          size="sm"
        />
      </div>
    </div>

    <div v-else class="text-center py-5 empty-state">
      <h5>Нет загруженных участников</h5>
      <p>Импортируйте участников из Excel или добавьте вручную</p>
    </div>

    <!-- Import modal -->
    <b-modal
      v-model="showImportModal"
      title="Импорт участников из Excel"
      size="lg"
      hide-footer
      modal-class="participants-import-modal"
      body-class="participants-import-modal__body"
    >
      <div
        :class="[
          'import-modal',
          layoutClass === 'dark-layout' ? 'import-modal--dark' : 'import-modal--light',
        ]"
      >
        <div class="import-modal__hero">
          <span class="import-modal__format-badge">Excel · .xlsx / .xls</span>
          <p class="import-modal__lede">
            Первая строка файла — служебная (английские имена полей), вторая — заголовки колонок на русском.
            Данные начинаются с третьей строки.
          </p>
        </div>

        <div class="import-modal__grid">
          <section class="import-modal__panel import-modal__panel--required" aria-labelledby="import-required-title">
            <h3 id="import-required-title" class="import-modal__panel-title">
              Обязательные столбцы
            </h3>
            <ul class="import-modal__list">
              <li class="import-modal__row">
                <span class="import-modal__row-label">Уникальный идентификатор участника</span>
                <div class="import-modal__tags">
                  <span class="import-col-tag">Номер</span>
                  <span class="import-col-tag">userId</span>
                  <span class="import-col-tag">ID</span>
                </div>
              </li>
              <li class="import-modal__row">
                <span class="import-modal__row-label">Фамилия</span>
                <div class="import-modal__tags">
                  <span class="import-col-tag">Фамилия</span>
                  <span class="import-col-tag">secondName</span>
                </div>
              </li>
              <li class="import-modal__row">
                <span class="import-modal__row-label">Чипы (несколько через запятую)</span>
                <div class="import-modal__tags">
                  <span class="import-col-tag">Чип</span>
                  <span class="import-col-tag">Чипы</span>
                  <span class="import-col-tag">chipIds</span>
                </div>
              </li>
            </ul>
          </section>

          <section class="import-modal__panel import-modal__panel--optional" aria-labelledby="import-optional-title">
            <h3 id="import-optional-title" class="import-modal__panel-title">
              Опциональные столбцы
            </h3>
            <ul class="import-modal__list import-modal__list--compact">
              <li class="import-modal__chip-row">
                <span class="import-col-tag">Имя</span>
                <span class="import-col-tag">name</span>
              </li>
              <li class="import-modal__chip-row">
                <span class="import-col-tag">Группа</span>
                <span class="import-col-tag">group</span>
              </li>
              <li class="import-modal__chip-row">
                <span class="import-col-tag">Команда</span>
                <span class="import-col-tag">team</span>
              </li>
              <li class="import-modal__chip-row">
                <span class="import-col-tag">Пол</span>
                <span class="import-col-tag">sex</span>
              </li>
              <li class="import-modal__chip-row">
                <span class="import-col-tag">Дистанция</span>
                <span class="import-col-tag">distance</span>
              </li>
              <li class="import-modal__chip-row">
                <span class="import-col-tag">Год рождения</span>
                <span class="import-col-tag">Дата рождения</span>
                <span class="import-col-tag">birthday</span>
              </li>
            </ul>
          </section>
        </div>

        <section class="import-modal__panel import-modal__panel--times" aria-label="Время старта и финиша">
          <h3 class="import-modal__panel-title import-modal__panel-title--inline">
            Старт и финиш (по желанию)
          </h3>
          <p class="import-modal__fine">
            Указывайте парой «дата + время». Поддерживаются форматы Excel:
            <span class="import-modal__mono">M/D/YY</span> и
            <span class="import-modal__mono">9:50:02 AM</span>, а также
            <span class="import-modal__mono">ДД.ММ.ГГГГ</span> и 24-часовое время.
            Время интерпретируется в часовом поясе, выбранном в конфигурации старта (по умолчанию Новосибирск).
          </p>
          <ul class="import-modal__time-pairs">
            <li>
              <span class="import-col-tag">Дата старта</span>
              <span class="import-col-tag">start_date</span>
              <span class="import-modal__pair-plus">+</span>
              <span class="import-col-tag">Стартовое время</span>
              <span class="import-col-tag">start_time</span>
            </li>
            <li>
              <span class="import-col-tag">Дата финиша</span>
              <span class="import-col-tag">end_date</span>
              <span class="import-modal__pair-plus">+</span>
              <span class="import-col-tag">Финишное время</span>
              <span class="import-col-tag">end_time</span>
            </li>
          </ul>
        </section>

        <div class="import-modal__example">
          <span class="import-modal__example-label">Пример чипов</span>
          <span class="import-col-tag import-col-tag--long">E2801170000001, E2801170000002</span>
        </div>

        <div class="import-modal__file">
          <label class="import-modal__file-label">Файл</label>
          <b-form-file
            v-model="excelFile"
            accept=".xlsx, .xls"
            placeholder="Выберите Excel файл..."
            drop-placeholder="Перетащите файл сюда..."
            @change="handleFileSelect"
          />
        </div>

        <div v-if="importedData.length > 0" class="import-modal__preview">
          <div class="import-modal__success-banner">
            <span class="import-modal__success-icon" aria-hidden="true">✓</span>
            <div>
              <strong class="import-modal__success-title">Готово к импорту</strong>
              <p class="import-modal__success-meta">
                Найдено <strong>{{ importedData.length }}</strong> участников — ниже превью первых строк.
              </p>
            </div>
          </div>

          <div class="import-modal__preview-table-wrap">
            <b-table
              :items="importedData.slice(0, 5)"
              :fields="previewFields"
              small
              striped
              class="import-modal__preview-table mb-0"
            />
          </div>

          <p v-if="importedData.length > 5" class="import-modal__preview-more">
            … и ещё {{ importedData.length - 5 }} участников
          </p>

          <div class="import-modal__actions">
            <b-button variant="outline-secondary" @click="cancelImport">
              Отмена
            </b-button>
            <b-button variant="primary" @click="confirmImport">
              Импортировать {{ importedData.length }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import { BCard, BButton, BTable, BFormInput, BFormSelect, BFormFile, BModal, BAlert, BPagination, VBTooltip } from 'bootstrap-vue'
import * as XLSX from 'xlsx'
import { mapDistanceNameToId, validateParticipantsDistances } from '../../utils/distance-mapper'
import {
  DEFAULT_TIMEZONE,
  startTimeIsoToInputValue,
  inputValueToStartTimeIso,
} from '../../utils/timezone'

/** API/DB may return chipIds as array, JSON string, or object — UI expects string[]. */
function normalizeChipIdsArray(raw) {
  if (raw == null || raw === '') return []
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return [String(raw)]
  }
  if (typeof raw === 'bigint') {
    return [String(raw)]
  }
  if (Array.isArray(raw)) {
    return raw.map(x => String(x).trim()).filter(s => s.length > 0)
  }
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t) return []
    if (t.startsWith('[')) {
      try {
        const p = JSON.parse(t)
        if (Array.isArray(p)) {
          return p.map(x => String(x).trim()).filter(s => s.length > 0)
        }
      } catch (e) {
        /* ignore */
      }
    }
    return t.split(',').map(s => s.trim()).filter(s => s.length > 0)
  }
  if (typeof raw === 'object') {
    return Object.values(raw).map(x => String(x).trim()).filter(s => s.length > 0)
  }
  return []
}

export default {
  name: 'ParticipantsList',
  components: {
    BCard,
    BButton,
    BTable,
    BFormInput,
    BFormSelect,
    BFormFile,
    BModal,
    BAlert,
    BPagination,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    participants: {
      type: Array,
      default: () => [],
    },
    availableDistances: {
      type: Array,
      default: () => [],
    },
    timezone: {
      type: String,
      default: DEFAULT_TIMEZONE,
    },
    invalidParticipantsFilterRequest: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      localParticipants: [],
      showImportModal: false,
      showDistanceWarningModal: false,
      excelFile: null,
      importedData: [],
      invalidDistanceParticipants: [],
      currentPage: 1,
      perPage: 20,
      participantSearchQuery: '',
      invalidDistanceOnly: false,
      sexOptions: [
        { value: '', text: '-' },
        { value: 'М', text: 'М' },
        { value: 'Ж', text: 'Ж' },
      ],
      tableFields: [
        {
          key: 'actions',
          label: '',
          thClass: 'participants-th participants-th--actions',
          tdClass: 'participants-td participants-td--actions',
        },
        {
          key: 'userId',
          label: 'Номер',
          thClass: 'participants-th participants-th--userId text-left',
          tdClass: 'participants-td participants-td--userId',
        },
        {
          key: 'secondName',
          label: 'Фамилия',
          thClass: 'participants-th participants-th--secondName text-left',
          tdClass: 'participants-td participants-td--secondName',
        },
        {
          key: 'name',
          label: 'Имя',
          thClass: 'participants-th participants-th--name text-left',
          tdClass: 'participants-td participants-td--name',
        },
        {
          key: 'chipIds',
          label: 'Чипы',
          thClass: 'participants-th participants-th--chips text-left',
          tdClass: 'participants-td participants-td--chips',
        },
        {
          key: 'group',
          label: 'Группа',
          thClass: 'participants-th participants-th--group text-left',
          tdClass: 'participants-td participants-td--group',
        },
        {
          key: 'team',
          label: 'Команда',
          thClass: 'participants-th participants-th--team text-left',
          tdClass: 'participants-td participants-td--team',
        },
        {
          key: 'sex',
          label: 'Пол',
          thClass: 'participants-th participants-th--sex text-left',
          tdClass: 'participants-td participants-td--sex',
        },
        {
          key: 'distance',
          label: 'Дистанция',
          thClass: 'participants-th participants-th--distance text-left',
          tdClass: 'participants-td participants-td--distance',
        },
        {
          key: 'birthday',
          label: 'Год рождения',
          thClass: 'participants-th participants-th--birthday text-left text-nowrap',
          tdClass: 'participants-td participants-td--birthday',
        },
        {
          key: 'startTime',
          label: 'Старт',
          thClass: 'participants-th participants-th--start text-left text-nowrap',
          tdClass: 'participants-td participants-td--start',
        },
      ],
      previewFields: [
        { key: 'userId', label: 'Номер' },
        { key: 'secondName', label: 'Фамилия' },
        { key: 'name', label: 'Имя' },
        { key: 'chipIdsString', label: 'Чипы' },
      ],
      layoutClass: 'light-layout',
      themeObserver: null,
    }
  },
  computed: {
    participantsWithDistanceFilter() {
      if (!this.invalidDistanceOnly) return this.localParticipants
      return this.localParticipants.filter(p => !p.distanceId)
    },

    filteredParticipants() {
      const raw = (this.participantSearchQuery || '').trim().toLowerCase()
      if (!raw) return this.participantsWithDistanceFilter
      return this.participantsWithDistanceFilter.filter((p) => this.participantMatchesSearch(p, raw))
    },

    displayedParticipants() {
      const list = this.filteredParticipants
      const start = (this.currentPage - 1) * this.perPage
      return list.slice(start, start + this.perPage)
    },

    totalPages() {
      return Math.ceil(this.filteredParticipants.length / this.perPage)
    },

    hasInvalidDistances() {
      return this.localParticipants.some(p => !p.distanceId)
    },

    invalidDistancesCount() {
      return this.localParticipants.filter(p => !p.distanceId).length
    },

    invalidDistanceParticipantsCount() {
      return this.localParticipants.filter(p => !p.distanceId).length
    },

    distanceOptions() {
      return this.availableDistances.map(d => ({
        value: d.id,
        text: d.name
      }))
    },
  },
  watch: {
    participants: {
      immediate: true,
      deep: true,
      handler(newVal) {
        const list = newVal || []
        this.localParticipants = list.map((p) => {
          const copy = JSON.parse(JSON.stringify(p))
          const ids = normalizeChipIdsArray(copy.chipIds ?? copy.chip_ids)
          return {
            ...copy,
            chipIds: ids,
            chipIdsString: ids.length ? ids.join(', ') : '',
          }
        })
      },
    },

    hasInvalidDistances: {
      immediate: true,
      handler(newVal) {
        this.$emit('validation-changed', {
          hasInvalidDistances: newVal,
          invalidCount: this.invalidDistancesCount
        })
      }
    },

    participantSearchQuery() {
      this.currentPage = 1
    },

    invalidParticipantsFilterRequest(newValue, oldValue) {
      if (!newValue || newValue === oldValue) return
      this.invalidDistanceOnly = true
      this.participantSearchQuery = ''
      this.currentPage = 1
      this.scrollToParticipantsTable()
    },

    'filteredParticipants.length'(len) {
      const totalPages = Math.ceil(len / this.perPage) || 1
      const maxPage = Math.max(1, totalPages)
      if (this.currentPage > maxPage) this.currentPage = maxPage
    },
  },
  mounted() {
    this.setupThemeObserver()
  },
  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
  },
  methods: {
    clearInvalidDistanceFilter() {
      this.invalidDistanceOnly = false
      this.currentPage = 1
    },

    scrollToParticipantsTable() {
      this.$nextTick(() => {
        const tableWrap = this.$refs.participantsTableWrap
        const card = this.$refs.participantsCard
        const target = tableWrap || card
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      })
    },

    participantMatchesSearch(p, q) {
      const chipJoined = Array.isArray(p.chipIds) ? p.chipIds.join(' ') : ''
      const haystack = [
        p.userId,
        p.secondName,
        p.name,
        p.chipIdsString,
        chipJoined,
        p.group,
        p.team,
        p.distance,
        p.birthday,
        p.sex,
      ]
        .map((x) => String(x ?? '').toLowerCase())
        .join(' ')
      return haystack.includes(q)
    },

    participantAtDisplayedRow(rowIndex) {
      return this.displayedParticipants[rowIndex]
    },

    detectTheme() {
      const html = document.documentElement
      const body = document.body
      
      if (html.classList.contains('dark-layout') || 
          body.classList.contains('dark-layout') ||
          html.classList.contains('theme-dark')) {
        this.layoutClass = 'dark-layout'
      } else {
        this.layoutClass = 'light-layout'
      }
    },

    setupThemeObserver() {
      this.detectTheme()
      
      const observerConfig = { attributes: true, attributeFilter: ['class'] }
      this.themeObserver = new MutationObserver(() => {
        this.detectTheme()
      })
      
      this.themeObserver.observe(document.documentElement, observerConfig)
      this.themeObserver.observe(document.body, observerConfig)
    },

    addParticipant() {
      this.localParticipants.push({
        userId: 'user' + Date.now(),
        secondName: '',
        name: '',
        chipIds: [],
        chipIdsString: '',
        startTime: undefined,
        endTime: undefined,
        group: '',
        team: '',
        sex: '',
        gender: '',
        distance: '',
        distanceId: null,
        birthday: '',
      })
      this.emitUpdate()
    },

    removeParticipant(index) {
      const participant = this.participantAtDisplayedRow(index)
      if (!participant) return
      const globalIndex = this.localParticipants.indexOf(participant)
      if (globalIndex === -1) return
      this.localParticipants.splice(globalIndex, 1)
      this.emitUpdate()
    },

    updateChipIds(index) {
      const participant = this.participantAtDisplayedRow(index)
      
      if (participant.chipIdsString) {
        participant.chipIds = participant.chipIdsString
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0)
      } else {
        participant.chipIds = []
      }
      
      this.emitUpdate()
    },

    handleDistanceInput(index) {
      const participant = this.participantAtDisplayedRow(index)
      if (!participant) return

      const mapping = mapDistanceNameToId(participant.distance || '', this.availableDistances)
      participant.distanceId = mapping.distanceId
      participant.distance = mapping.distanceName || participant.distance
      this.emitUpdate()
    },

    participantStartTimeInputValue(item) {
      if (!item.startTime) return ''
      const d = new Date(item.startTime)
      if (Number.isNaN(d.getTime())) return ''
      return startTimeIsoToInputValue(d.toISOString(), this.timezone)
    },

    onParticipantStartTimeInput(pageRowIndex, value) {
      const p = this.participantAtDisplayedRow(pageRowIndex)
      if (!p) return
      const iso = inputValueToStartTimeIso(value, this.timezone)
      p.startTime = iso ? new Date(iso) : undefined
      this.emitUpdate()
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          
          // Skip first row (English headers) and use second row (Russian headers)
          const range = XLSX.utils.decode_range(worksheet['!ref'])
          range.s.r = 1 // Пропускаем первую строку
          worksheet['!ref'] = XLSX.utils.encode_range(range)
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: '',
            raw: false,
          })

          this.importedData = jsonData.map(row => this.mapExcelRowToParticipant(row))
        } catch (error) {
          console.error('Error parsing Excel:', error)
          this.$bvToast.toast('Ошибка чтения файла: ' + error.message, {
            title: 'Ошибка',
            variant: 'danger',
            solid: true,
          })
        }
      }
      reader.readAsArrayBuffer(file)
    },

    mapExcelRowToParticipant(row) {
      // Support both English and Russian headers
      const chipIdsString = (
        row['chip'] || row['Чип'] || row['Чипы'] || 
        row['chipIds'] || row['Chip IDs'] || ''
      ).toString()
      
      const chipIds = chipIdsString
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0)

      const distanceName = row['distance'] || row['Дистанция'] || row['Distance'] || ''
      
      // Map distance name to ID using smart matching
      const distanceMapping = mapDistanceNameToId(distanceName, this.availableDistances)

      // Normalize sex/gender field
      const sexRaw = row['sex'] || row['Пол'] || row['Gender'] || ''
      const sex = this.normalizeSex(sexRaw)

      const birthdayValue = row['birthday'] || row['Год рождения'] || row['Дата рождения'] || row['Birthday'] || ''
      
      // Parse start time from date + time columns
      const startDate = row['Дата старта'] || row['start_date'] || ''
      const startTimeCol = row['Стартовое время'] || row['start_time'] || ''
      const startTime = this.parseDateTime(startDate, startTimeCol)
      
      // Parse end time from date + time columns  
      const endDate = row['Дата финиша'] || row['end_date'] || startDate // Use start date if end date not specified
      const endTimeCol = row['Финишное время'] || row['end_time'] || ''
      const endTime = this.parseDateTime(endDate, endTimeCol)

      return {
        userId: (
          row['number'] || row['Номер'] || 
          row['userId'] || row['ID'] || row['id'] || 
          'user' + Date.now()
        ).toString(),
        secondName: row['surname'] || row['Фамилия'] || row['secondName'] || row['LastName'] || '',
        name: row['name'] || row['Имя'] || row['FirstName'] || '',
        chipIds: chipIds,
        chipIdsString: chipIdsString,
        startTime: startTime,
        endTime: endTime,
        group: row['group'] || row['Группа'] || row['Group'] || '',
        team: row['team'] || row['Команда'] || row['Team'] || '',
        sex: sex,
        gender: sex, // Backend uses 'gender' field
        distance: distanceMapping.distanceName || distanceName,
        distanceId: distanceMapping.distanceId,
        distanceMatched: distanceMapping.matched,
        birthday: birthdayValue ? birthdayValue.toString() : '',
      }
    },

    normalizeSex(value) {
      if (!value) return ''
      const normalized = value.toString().toLowerCase().trim()
      
      // Map full words to short codes
      if (normalized.includes('муж') || normalized === 'm' || normalized === 'м') {
        return 'М'
      }
      if (normalized.includes('жен') || normalized === 'f' || normalized === 'ж' || normalized.includes('female')) {
        return 'Ж'
      }
      
      return value
    },

    /**
     * Wall time in Asia/Novosibirsk (UTC+7) stored as Date.UTC(..., h-7, ...).
     */
    combineNskDateTime(year, month, day, hours, minutes, seconds) {
      const y = Number(year)
      const mo = Number(month)
      const d = Number(day)
      const h = Number(hours)
      const mi = Number(minutes)
      const s = Number(seconds) || 0
      if ([y, mo, d, h, mi, s].some((n) => Number.isNaN(n))) return undefined
      const dt = new Date(Date.UTC(y, mo - 1, d, h - 7, mi, s))
      return Number.isNaN(dt.getTime()) ? undefined : dt
    },

    /** Excel 1900 date serial (+ optional fractional day) → JS Date (UTC instant). */
    dateFromExcelSerial(serial) {
      if (typeof serial !== 'number' || !Number.isFinite(serial)) return undefined
      const ms = (serial - 25569) * 86400 * 1000
      const dt = new Date(ms)
      return Number.isNaN(dt.getTime()) ? undefined : dt
    },

    parseTimeComponents(timeStr) {
      const raw = String(timeStr).trim()
      const m = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?$/i)
      if (!m) return null
      let h = parseInt(m[1], 10)
      const min = parseInt(m[2], 10)
      const sec = m[3] != null ? parseInt(m[3], 10) : 0
      const ap = m[4] ? m[4].replace(/\./g, '').toLowerCase() : ''
      if (ap.startsWith('p') && h < 12) h += 12
      if (ap.startsWith('a') && h === 12) h = 0
      if (h < 0 || h > 23 || min < 0 || min > 59 || sec < 0 || sec > 59) return null
      return { hours: h, minutes: min, seconds: sec }
    },

    /**
     * Slash or dash: M/D/YY (Excel US) or D/M/Y when first part > 12.
     */
    parseSlashDateParts(dateStr) {
      const parts = String(dateStr)
        .trim()
        .split(/[/-]/)
        .map((x) => parseInt(x, 10))
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
      let [a, b, y] = parts
      if (y < 100) y += 2000
      let month
      let day
      if (a > 12) {
        day = a
        month = b
      } else if (b > 12) {
        month = a
        day = b
      } else {
        month = a
        day = b
      }
      if (month < 1 || month > 12 || day < 1 || day > 31) return null
      return { year: y, month, day }
    },

    parseDateTime(dateStr, timeStr) {
      const missingDate = dateStr === '' || dateStr === null || dateStr === undefined
      const missingTime = timeStr === '' || timeStr === null || timeStr === undefined
      if (missingDate || missingTime) return undefined

      try {
        if (typeof dateStr === 'number' && typeof timeStr === 'number') {
          if (timeStr >= 0 && timeStr < 1) {
            const base = this.dateFromExcelSerial(Math.trunc(dateStr))
            if (!base) return undefined
            const totalSeconds = Math.min(86399, Math.round(timeStr * 86400))
            const hours = Math.floor(totalSeconds / 3600)
            const minutes = Math.floor((totalSeconds % 3600) / 60)
            const seconds = totalSeconds % 60
            return this.combineNskDateTime(
              base.getUTCFullYear(),
              base.getUTCMonth() + 1,
              base.getUTCDate(),
              hours,
              minutes,
              seconds,
            )
          }
          return this.dateFromExcelSerial(dateStr + timeStr)
        }

        if (typeof dateStr === 'number' && typeof timeStr === 'string') {
          const base = this.dateFromExcelSerial(dateStr)
          if (!base) return undefined
          const t = this.parseTimeComponents(timeStr)
          if (!t) return undefined
          return this.combineNskDateTime(
            base.getUTCFullYear(),
            base.getUTCMonth() + 1,
            base.getUTCDate(),
            t.hours,
            t.minutes,
            t.seconds,
          )
        }

        const ds = String(dateStr).trim()
        const ts = String(timeStr).trim()
        if (!ds || !ts) return undefined

        const t = this.parseTimeComponents(ts)
        if (!t) return undefined

        if (ds.includes('.')) {
          const seg = ds.split('.').map((x) => parseInt(x.trim(), 10))
          if (seg.length !== 3 || seg.some((n) => Number.isNaN(n))) return undefined
          let [day, month, year] = seg
          if (year < 100) year += 2000
          return this.combineNskDateTime(year, month, day, t.hours, t.minutes, t.seconds)
        }

        if (ds.includes('/') || ds.includes('-')) {
          const ymd = this.parseSlashDateParts(ds)
          if (!ymd) return undefined
          return this.combineNskDateTime(
            ymd.year,
            ymd.month,
            ymd.day,
            t.hours,
            t.minutes,
            t.seconds,
          )
        }

        return undefined
      } catch (e) {
        console.error('Error parsing date/time:', dateStr, timeStr, e)
        return undefined
      }
    },

    confirmImport() {
      // Replace or append?
      const shouldReplace = confirm('Заменить существующих участников? (Отмена = добавить к существующим)')
      
      if (shouldReplace) {
        this.localParticipants = [...this.importedData]
      } else {
        this.localParticipants.push(...this.importedData)
      }

      this.emitUpdate()
      this.cancelImport()

      this.$bvToast.toast('Импортировано участников: ' + this.importedData.length, {
        title: 'Успешно',
        variant: 'success',
        solid: true,
      })
    },

    cancelImport() {
      this.showImportModal = false
      this.excelFile = null
      this.importedData = []
    },

    emitUpdate() {
      // Normalize data before emitting
      const normalized = this.localParticipants.map(p => ({
        ...p,
        gender: p.gender || p.sex, // Ensure gender is set
        birthday: p.birthday ? p.birthday.toString() : '',
        startTime: (() => {
          if (p.startTime === null || p.startTime === undefined || p.startTime === '') return undefined
          const d = new Date(p.startTime)
          return Number.isNaN(d.getTime()) ? undefined : d
        })(),
        endTime: (() => {
          if (p.endTime === null || p.endTime === undefined || p.endTime === '') return undefined
          const d = new Date(p.endTime)
          return Number.isNaN(d.getTime()) ? undefined : d
        })(),
      }))
      
      this.$emit('update', normalized)
    },
  },
}
</script>

<style scoped>
.participant-start-time-input {
  min-width: 0;
  width: 100%;
}

/* —— Import modal (structured layout, readable contrast) —— */
.import-modal {
  font-size: 0.9375rem;
  line-height: 1.55;
}

.import-modal__hero {
  margin-bottom: 1.25rem;
}

.import-modal__format-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.35rem 0.75rem;
  border-radius: 2rem;
  margin-bottom: 0.75rem;
}

.import-modal__lede {
  margin: 0;
  font-size: 0.875rem;
}

.import-modal__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

@media (max-width: 767.98px) {
  .import-modal__grid {
    grid-template-columns: 1fr;
  }
}

.import-modal__panel {
  border-radius: 0.5rem;
  padding: 1rem 1.125rem;
  border: 1px solid transparent;
}

.import-modal__panel-title {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 700;
  margin: 0 0 0.875rem;
}

.import-modal__panel-title--inline {
  margin-bottom: 0.5rem;
}

.import-modal__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.import-modal__row {
  padding: 0.65rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.import-modal__row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.import-modal__row:first-child {
  padding-top: 0;
}

.import-modal__row-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
}

.import-modal__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.import-col-tag {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.5rem;
  border-radius: 0.28rem;
  line-height: 1.3;
}

.import-col-tag--long {
  max-width: 100%;
  word-break: break-all;
}

.import-modal__list--compact {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.import-modal__chip-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.import-modal__panel--times {
  margin-bottom: 1rem;
}

.import-modal__fine {
  margin: 0 0 0.75rem;
  font-size: 0.8125rem;
}

.import-modal__mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
}

.import-modal__time-pairs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.import-modal__time-pairs li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.import-modal__pair-plus {
  font-weight: 600;
  opacity: 0.45;
  font-size: 0.875rem;
  padding: 0 0.15rem;
}

.import-modal__example {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

.import-modal__example-label {
  font-size: 0.8125rem;
  font-weight: 600;
}

.import-modal__file-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.4rem;
  opacity: 0.85;
}

.import-modal__file {
  margin-bottom: 0.25rem;
}

.import-modal__preview {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.import-modal__success-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.125rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
}

.import-modal__success-icon {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 700;
}

.import-modal__success-title {
  display: block;
  font-size: 0.9375rem;
  margin-bottom: 0.25rem;
}

.import-modal__success-meta {
  margin: 0;
  font-size: 0.8125rem;
  opacity: 0.92;
}

.import-modal__preview-table-wrap {
  border-radius: 0.428rem;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 0.5rem;
}

.import-modal__preview-more {
  text-align: center;
  font-size: 0.8125rem;
  margin: 0 0 1rem;
  opacity: 0.75;
}

.import-modal__actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Light */
.import-modal--light {
  color: #3f4054;
}

.import-modal--light .import-modal__format-badge {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.25);
}

.import-modal--light .import-modal__lede {
  color: #6e6b7b;
}

.import-modal--light .import-modal__panel {
  background: #f8f8fa;
  border-color: #e8e9f0;
}

.import-modal--light .import-modal__panel--required {
  border-left: 3px solid #ea5455;
  background: linear-gradient(90deg, rgba(234, 84, 85, 0.06) 0%, #f8f8fa 12px);
}

.import-modal--light .import-modal__panel--optional {
  border-left: 3px solid #7367f0;
  background: linear-gradient(90deg, rgba(115, 103, 240, 0.06) 0%, #f8f8fa 12px);
}

.import-modal--light .import-modal__panel--times {
  background: #faf9ff;
  border: 1px solid #ebe9fe;
  border-left: 3px solid #7367f0;
}

.import-modal--light .import-modal__panel-title {
  color: #8b8c9a;
}

.import-modal--light .import-modal__row-label {
  color: #5e5873;
}

.import-modal--light .import-col-tag {
  background: #fff;
  color: #4b4b5a;
  border: 1px solid #d8d6de;
}

.import-modal--light .import-modal__fine {
  color: #6e6b7b;
}

.import-modal--light .import-modal__mono {
  background: #eef0f4;
  color: #3f4054;
}

.import-modal--light .import-modal__example {
  background: #f4f5f8;
  border: 1px solid #e8e9f0;
}

.import-modal--light .import-modal__example-label {
  color: #5e5873;
}

.import-modal--light .import-modal__success-banner {
  background: #e8f9ef;
  border-color: rgba(40, 199, 111, 0.35);
}

.import-modal--light .import-modal__success-icon {
  background: #28c76f;
  color: #fff;
}

.import-modal--light .import-modal__success-title {
  color: #1e5c3a;
}

.import-modal--light .import-modal__success-meta {
  color: #2d6b47;
}

.import-modal--light .import-modal__preview {
  border-top-color: #ebe9f1;
}

/* Dark */
.import-modal--dark .import-modal__format-badge {
  background: linear-gradient(135deg, #8b7ff0 0%, #6c5ce7 100%);
  color: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}

.import-modal--dark .import-modal__lede {
  color: #a8aaae;
}

.import-modal--dark .import-modal__panel {
  background: rgba(37, 43, 66, 0.65);
  border-color: #3b4253;
}

.import-modal--dark .import-modal__panel--required {
  border-left-color: #ff8a9b;
  background: linear-gradient(90deg, rgba(234, 84, 85, 0.12) 0%, rgba(37, 43, 66, 0.65) 14px);
}

.import-modal--dark .import-modal__panel--optional {
  border-left-color: #a89ff7;
  background: linear-gradient(90deg, rgba(157, 147, 246, 0.15) 0%, rgba(37, 43, 66, 0.65) 14px);
}

.import-modal--dark .import-modal__panel--times {
  background: rgba(115, 103, 240, 0.1);
  border-color: rgba(157, 147, 246, 0.25);
  border-left: 3px solid #9d93f6;
}

.import-modal--dark .import-modal__panel-title {
  color: #9b9dad;
}

.import-modal--dark .import-modal__row {
  border-bottom-color: rgba(59, 66, 83, 0.7);
}

.import-modal--dark .import-modal__row-label {
  color: #e4e5ea;
}

.import-modal--dark .import-col-tag {
  background: rgba(26, 30, 52, 0.95);
  color: #d5d6de;
  border-color: #4a5168;
}

.import-modal--dark .import-modal__fine {
  color: #a8aaae;
}

.import-modal--dark .import-modal__mono {
  background: rgba(26, 30, 52, 0.9);
  color: #d0d2d6;
}

.import-modal--dark .import-modal__example {
  background: rgba(37, 43, 66, 0.5);
  border: 1px solid #3b4253;
}

.import-modal--dark .import-modal__example-label {
  color: #d0d2d6;
}

.import-modal--dark .import-modal__success-banner {
  background: rgba(40, 199, 111, 0.12);
  border-color: rgba(40, 199, 111, 0.35);
}

.import-modal--dark .import-modal__success-icon {
  background: #28c76f;
  color: #fff;
}

.import-modal--dark .import-modal__success-title {
  color: #e8fdf0;
}

.import-modal--dark .import-modal__success-meta {
  color: #b8e8c8;
}

.import-modal--dark .import-modal__preview {
  border-top-color: #3b4253;
}

.import-modal--dark .import-modal__preview-table-wrap {
  border-color: #3b4253;
}

.import-modal--dark .import-modal__preview-more {
  color: #a8aaae;
}

.table-responsive {
  max-height: 500px;
  overflow-x: auto;
  overflow-y: auto;
  border-radius: 0.428rem;
  transition: all 0.3s ease;
}

/* Light theme - table */
.participants-card.light-layout .table-responsive {
  background: #fafafa;
}

.participants-card.light-layout .table {
  background: transparent;
}

.participants-card.light-layout .table thead th {
  background: #f8f8f8;
  color: #5e5873;
  border-color: #ebe9f1;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.8px;
}

.participants-card.light-layout .table tbody tr {
  background: #fff;
  border-color: #ebe9f1;
  transition: all 0.2s ease;
}

.participants-card.light-layout .table tbody tr:hover {
  background: #f8f8f8;
  box-shadow: 0 2px 8px 0 rgba(115, 103, 240, 0.1);
}

.participants-card.light-layout .table td {
  border-color: #ebe9f1;
}

/* Dark theme - table */
.participants-card.dark-layout .table-responsive {
  background: rgba(26, 30, 52, 0.3);
}

.participants-card.dark-layout .table {
  background: transparent;
  color: #d0d2d6;
}

.participants-card.dark-layout .table thead th {
  background: rgba(37, 43, 66, 0.5);
  color: #a8aaae;
  border-color: #3b4253;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.8px;
}

.participants-card.dark-layout .table tbody tr {
  background: rgba(37, 43, 66, 0.4);
  border-color: rgba(59, 66, 83, 0.5);
  transition: all 0.2s ease;
}

.participants-card.dark-layout .table tbody tr:hover {
  background: rgba(157, 147, 246, 0.1);
  box-shadow: 0 2px 8px 0 rgba(157, 147, 246, 0.2);
}

.participants-card.dark-layout .table td {
  border-color: rgba(59, 66, 83, 0.5);
  color: #d0d2d6;
}

/* Buttons in table */
.participants-card.dark-layout .table .btn-danger {
  background: linear-gradient(135deg, #ea5455, #d63031);
  border-color: transparent;
  box-shadow: 0 2px 4px 0 rgba(234, 84, 85, 0.3);
}

.participants-card.dark-layout .table .btn-danger:hover {
  background: linear-gradient(135deg, #f76c6d, #ea5455);
  box-shadow: 0 4px 8px 0 rgba(234, 84, 85, 0.5);
  transform: scale(1.05);
}

.participants-card.light-layout .table .btn-danger:hover {
  transform: scale(1.05);
}

/* Add participant button */
.participants-card.dark-layout .btn-success {
  background: linear-gradient(135deg, #39da8a, #28c76f);
  border-color: transparent;
  box-shadow: 0 2px 6px 0 rgba(40, 199, 111, 0.3);
}

.participants-card.dark-layout .btn-success:hover {
  background: linear-gradient(135deg, #5ede9c, #39da8a);
  box-shadow: 0 4px 12px 0 rgba(57, 218, 138, 0.5);
  transform: translateY(-1px);
}

.participants-card.dark-layout .btn-primary {
  background: linear-gradient(135deg, #9d93f6, #7367f0);
  border-color: transparent;
  box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.3);
}

.participants-card.dark-layout .btn-primary:hover {
  background: linear-gradient(135deg, #b4a9ff, #9d93f6);
  box-shadow: 0 4px 12px 0 rgba(157, 147, 246, 0.5);
  transform: translateY(-1px);
}

/* Empty state */
.participants-card.light-layout .empty-state {
  color: #b9b9c3;
}

.participants-card.light-layout .empty-state h5 {
  color: #5e5873;
}

.participants-card.dark-layout .empty-state {
  color: #a8aaae;
}

.participants-card.dark-layout .empty-state h5 {
  color: #d0d2d6;
}

/* Pagination */
.participants-card.dark-layout >>> .page-link {
  background: rgba(37, 43, 66, 0.6);
  border-color: #3b4253;
  color: #d0d2d6;
}

.participants-card.dark-layout >>> .page-link:hover {
  background: rgba(157, 147, 246, 0.2);
  border-color: #9d93f6;
  color: #fff;
}

.participants-card.dark-layout >>> .page-item.active .page-link {
  background: linear-gradient(135deg, #9d93f6, #7367f0);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.4);
}

.participants-card.dark-layout >>> .page-item.disabled .page-link {
  background: rgba(37, 43, 66, 0.3);
  border-color: rgba(59, 66, 83, 0.3);
  color: rgba(168, 170, 174, 0.4);
}

.participants-toolbar {
  display: grid;
  grid-template-columns: minmax(0, max-content) minmax(10rem, 1fr) auto;
  align-items: center;
  gap: 0.75rem 1.25rem;
  width: 100%;
}

.participants-toolbar__left {
  min-width: 0;
}

.participants-toolbar__search {
  min-width: 0;
}

.participants-toolbar__buttons {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
}

.participants-toolbar__btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.participants-toolbar__filter-meta {
  font-size: 0.875rem;
  margin-left: 0.25rem;
}

.participants-search-input {
  width: 100%;
  min-width: 0;
}

@media (max-width: 991.98px) {
  .participants-toolbar {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .participants-toolbar__buttons {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .participants-toolbar__btn {
    flex: 1 1 auto;
    min-width: min(100%, 11rem);
  }
}

.participants-search-empty {
  font-size: 0.9375rem;
}

/* —— Table: fixed layout, column widths, aligned controls —— */
.participants-card .participants-table {
  table-layout: fixed;
  width: 100%;
}

.participants-card .participants-table .participants-th,
.participants-card .participants-table .participants-td {
  vertical-align: middle;
}

.participants-card .participants-table .participants-td .form-control,
.participants-card .participants-table .participants-td .custom-select {
  width: 100%;
  min-width: 0;
}

.participants-cell-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(1.5em + 0.5rem + 2px);
}

.participants-btn-remove {
  line-height: 1;
  padding: 0.28rem 0.42rem;
}

.participants-th--actions,
.participants-td--actions {
  width: 3rem;
  max-width: 3rem;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.participants-th--userId,
.participants-td--userId {
  width: 5.25rem;
}

.participants-th--secondName,
.participants-td--secondName {
  width: 11%;
}

.participants-th--name,
.participants-td--name {
  width: 10%;
}

.participants-th--chips,
.participants-td--chips {
  width: 14%;
}

.participants-th--group,
.participants-td--group {
  width: 13%;
}

.participants-th--team,
.participants-td--team {
  width: 13%;
}

.participants-th--sex,
.participants-td--sex {
  width: 3.75rem;
  max-width: 4.25rem;
}

.participants-th--distance,
.participants-td--distance {
  width: 6.75rem;
}

.participants-th--birthday,
.participants-td--birthday {
  width: 5.5rem;
}

.participants-th--start,
.participants-td--start {
  width: 11.25rem;
}

.participants-card .participants-table .participants-td--group .form-control,
.participants-card .participants-table .participants-td--team .form-control,
.participants-card .participants-table .participants-td--chips .form-control {
  word-break: break-word;
}

.hints-alert {
  padding: 1.143rem !important;
}

.hint-icon {
  font-size: 1.5rem;
  line-height: 1;
  margin-right: 1rem;
  flex-shrink: 0;
}

.hint-content {
  flex: 1;
}

.hint-title {
  font-weight: 600;
  margin-bottom: 0.857rem;
  font-size: 0.95rem;
}

.hint-list {
  display: flex;
  flex-direction: column;
  gap: 0.571rem;
}

.hint-list-item {
  padding-left: 1rem;
  position: relative;
  font-size: 0.857rem;
  line-height: 1.6;
}

.hint-list-item::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #00cfe8;
  font-weight: bold;
}

.hint-list-item strong {
  font-weight: 600;
}

.hint-list-item code,
.alert code {
  padding: 0.143rem 0.428rem;
  border-radius: 0.286rem;
  font-size: 0.857rem;
  font-weight: 500;
}

/* Light theme */
.participants-card.light-layout .hint-title {
  color: #5e5873;
}

.participants-card.light-layout .hint-list-item {
  color: #6e6b7b;
}

.participants-card.light-layout .hint-list-item strong {
  color: #5e5873;
}

.participants-card.light-layout .hint-list-item code,
.participants-card.light-layout .alert code {
  background: rgba(115, 103, 240, 0.08);
  color: #7367f0;
  border: 1px solid rgba(115, 103, 240, 0.15);
}

/* Dark theme */
.participants-card.dark-layout .hint-title {
  color: #fff;
}

.participants-card.dark-layout .hint-list-item {
  color: #d0d2d6;
}

.participants-card.dark-layout .hint-list-item strong {
  color: #fff;
}

.participants-card.dark-layout .hint-list-item code,
.participants-card.dark-layout .alert code {
  background: rgba(157, 147, 246, 0.15);
  color: #9d93f6;
  border: 1px solid rgba(157, 147, 246, 0.25);
}
</style>

<style>
/* Import modal shell */
.participants-import-modal .modal-content {
  border: none;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(34, 41, 47, 0.14);
}

.participants-import-modal__body {
  padding: 1.25rem 1.5rem 1.5rem;
}

.dark-layout .participants-import-modal .modal-content {
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
}

/* Modal styling for dark theme - using global styles to override Bootstrap-Vue */
.dark-layout .modal-content {
  background: linear-gradient(135deg, #252b42, #1e2439) !important;
  border: 1px solid #3b4253 !important;
  box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.5) !important;
}

.dark-layout .modal-header {
  background: rgba(37, 43, 66, 0.7) !important;
  border-bottom: 1px solid #3b4253 !important;
}

.dark-layout .modal-header .modal-title {
  color: #fff !important;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.dark-layout .modal-header .close {
  color: #d0d2d6 !important;
  text-shadow: none !important;
  opacity: 0.8;
}

.dark-layout .modal-header .close:hover {
  color: #fff !important;
  opacity: 1;
}

.dark-layout .modal-body {
  background: transparent !important;
  color: #d0d2d6 !important;
}

.dark-layout .modal-footer {
  background: rgba(37, 43, 66, 0.5) !important;
  border-top: 1px solid #3b4253 !important;
}

/* Alert in modal - dark theme */
.dark-layout .modal-body .alert {
  background: rgba(37, 43, 66, 0.6) !important;
  border-color: rgba(59, 66, 83, 0.8) !important;
  color: #d0d2d6 !important;
}

.dark-layout .modal-body .alert.alert-info {
  background: rgba(0, 207, 232, 0.1) !important;
  border-color: rgba(0, 207, 232, 0.3) !important;
}

.dark-layout .modal-body .alert.alert-success {
  background: rgba(40, 199, 111, 0.1) !important;
  border-color: rgba(40, 199, 111, 0.3) !important;
}

.dark-layout .modal-body .alert.alert-danger {
  background: rgba(234, 84, 85, 0.1) !important;
  border-color: rgba(234, 84, 85, 0.3) !important;
}

.dark-layout .modal-body .alert strong {
  color: #fff !important;
}

.dark-layout .modal-body .alert ul {
  margin-bottom: 0;
}

.dark-layout .modal-body .alert li {
  color: #d0d2d6 !important;
}

.dark-layout .modal-body .alert code {
  background: rgba(157, 147, 246, 0.2) !important;
  color: #ff6b9d !important;
  border: 1px solid rgba(255, 107, 157, 0.3) !important;
  padding: 0.2rem 0.5rem;
  border-radius: 0.3rem;
  font-weight: 600;
  font-size: 0.85rem;
}

/* Form file input - dark theme */
.dark-layout .modal-body .custom-file-label {
  background: rgba(26, 30, 52, 0.6) !important;
  border-color: #3b4253 !important;
  color: #a8aaae !important;
}

.dark-layout .modal-body .custom-file-label:hover {
  border-color: #9d93f6 !important;
}

.dark-layout .modal-body .custom-file-label::after {
  background: rgba(157, 147, 246, 0.2) !important;
  border-left: 1px solid #3b4253 !important;
  color: #d0d2d6 !important;
}

/* Preview table in modal - dark theme */
.dark-layout .modal-body .table {
  background: transparent !important;
  color: #d0d2d6 !important;
}

.dark-layout .modal-body .table thead th {
  background: rgba(37, 43, 66, 0.6) !important;
  border-color: #3b4253 !important;
  color: #a8aaae !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.8px;
}

.dark-layout .modal-body .table tbody tr {
  background: rgba(37, 43, 66, 0.3) !important;
  border-color: rgba(59, 66, 83, 0.5) !important;
}

.dark-layout .modal-body .table tbody tr:hover {
  background: rgba(157, 147, 246, 0.1) !important;
}

.dark-layout .modal-body .table td {
  border-color: rgba(59, 66, 83, 0.5) !important;
  color: #d0d2d6 !important;
}

/* Text in modal */
.dark-layout .modal-body .text-muted {
  color: #a8aaae !important;
}

.dark-layout .modal-body .text-center {
  color: #d0d2d6 !important;
}

/* Buttons in modal - dark theme */
.dark-layout .modal-body .btn-secondary {
  background: rgba(130, 134, 139, 0.3) !important;
  border-color: #3b4253 !important;
  color: #d0d2d6 !important;
}

.dark-layout .modal-body .btn-secondary:hover {
  background: rgba(130, 134, 139, 0.5) !important;
  border-color: #9d93f6 !important;
  color: #fff !important;
  transform: translateY(-1px);
}

.dark-layout .modal-body .btn-primary {
  background: linear-gradient(135deg, #9d93f6, #7367f0) !important;
  border-color: transparent !important;
  box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.4) !important;
}

.dark-layout .modal-body .btn-primary:hover {
  background: linear-gradient(135deg, #b4a9ff, #9d93f6) !important;
  box-shadow: 0 4px 12px 0 rgba(157, 147, 246, 0.6) !important;
  transform: translateY(-1px);
}
</style>